import type { AlertPolicyView } from "@closer/shared";
import { Badge, Button, Card, Checkbox, PageHeader, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * KRİTİK UYARI POLİTİKASI (CLOS-1260 — Bekleyen İşlemler Kanonu §19).
 *
 * ### ⚠ Ekran kanonun kendi ekranı
 *
 * §19 yönetici arayüzünü birebir gösteriyor: *"Bilgilendir: Sözleşme Yöneticisi · Ana Ekip ·
 * Birlikte Çalışan Ekipler"* ve *"Kritik olduğunda ayrıca: Organizasyon Yönetimi"*. Dört
 * kutu, dört alan — arada bir çeviri katmanı yok.
 *
 * ### ⚠ GÖRME ile DEĞİŞTİRME ayrı yetkiler
 *
 * Okuma `organization.view`: hangi uyarının kime gittiği bir sır değil, kullanıcının
 * bilmesi gereken bir kural. Değiştirme `organization.security` — güvenlik politikasıyla
 * aynı çizgi.
 *
 * ### ⚠ Politika ERİŞİM AÇMAZ
 *
 * Bir kutuyu işaretlemek kimseye sözleşmeyi görme hakkı vermiyor: hatırlatma yolu her aday
 * için kayıt erişimini ayrıca soruyor. Ekranda da söyleniyor — yönetici "işaretledim ama
 * gitmemiş" durumunu sebebiyle anlayabilmeli.
 */

const KUTULAR = [
  {
    alan: "informContractManager",
    etiket: "Sözleşme Yöneticisi",
    aciklama: "Sözleşmenin sorumlusu ve süre/yenileme sorumlusu.",
  },
  {
    alan: "informLeadTeam",
    etiket: "Ana Ekip",
    aciklama: "Sözleşmeye ana ekip olarak katılan departmanların üyeleri.",
  },
  {
    alan: "informCollaboratingTeams",
    etiket: "Birlikte Çalışan Ekipler",
    aciklama: "Destek veren departmanların üyeleri.",
  },
] as const;

type Alan = (typeof KUTULAR)[number]["alan"] | "escalateToOrgManagementOnCritical";

export function AlertPolicyPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "uyari-politikasi"),
    queryFn: async () =>
      (await api<AlertPolicyView>(`/api/tenants/${tenantId}/alert-policy`, { tenantId })).data,
  });

  /**
   * ⚠ TASLAK YEREL, kaydetme AÇIK bir eylem.
   *
   * Her kutuda anında istek atsaydık, dört kutuyu değiştiren yönetici dört ayrı politika
   * sürümü yazardı ve denetim izinde üç tanesi hiç istenmemiş ara hâl olurdu.
   */
  const [taslak, setTaslak] = useState<Record<Alan, boolean> | null>(null);
  useEffect(() => {
    if (!sorgu.data) return;
    setTaslak({
      informContractManager: sorgu.data.informContractManager,
      informLeadTeam: sorgu.data.informLeadTeam,
      informCollaboratingTeams: sorgu.data.informCollaboratingTeams,
      escalateToOrgManagementOnCritical: sorgu.data.escalateToOrgManagementOnCritical,
    });
  }, [sorgu.data]);

  const kaydet = useMutation({
    mutationFn: async (politika: Record<Alan, boolean>) =>
      (
        await api<AlertPolicyView>(`/api/tenants/${tenantId}/alert-policy`, {
          method: "PUT",
          tenantId,
          body: JSON.stringify(politika),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "uyari-politikasi") });
      toast.success("Uyarı politikası kaydedildi");
    },
    onError: (error) =>
      toast.error(error instanceof ApiRequestError ? error.message : "Politika kaydedilemedi"),
  });

  const degistirebilir = can("organization.security");

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title="Uyarı Politikası"
        description="Yenileme ve kritik tarih uyarılarının kime gideceğini belirleyin."
      />

      <div className="w-full max-w-2xl">
        <Card
          title="Yenileme ve kritik tarih uyarıları"
          actions={
            sorgu.isPending || !degistirebilir || !taslak ? null : (
              <Button
                type="button"
                size="sm"
                loading={kaydet.isPending}
                onClick={() => kaydet.mutate(taslak)}
              >
                Kaydet
              </Button>
            )
          }
        >
          {sorgu.isPending || !taslak ? (
            <div role="status" aria-label="Politika yükleniyor">
              <Skeleton lines={4} />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                {/* ⚠ "Dokunulmadı" ile "dokunuldu ve aynı seçildi" farklı şeyler — yönetici için. */}
                <Badge tone={sorgu.data?.customized ? "info" : "neutral"}>
                  {sorgu.data?.customized ? "Özelleştirildi" : "Varsayılan"}
                </Badge>
              </div>

              <fieldset className="flex flex-col gap-3">
                <legend className="text-sm font-medium text-fg-strong">Bilgilendir</legend>
                {/* ⚠ Açıklama kutunun ALTINDA: `Checkbox` açıklama taşımıyor ve bileşene
                    alan eklemek bu kartın işi değil (packages/ui kendi süreci). */}
                {KUTULAR.map((kutu) => (
                  <div key={kutu.alan} className="flex flex-col gap-1">
                    <Checkbox
                      label={kutu.etiket}
                      checked={taslak[kutu.alan]}
                      disabled={!degistirebilir}
                      onChange={(e) =>
                        setTaslak({ ...taslak, [kutu.alan]: e.currentTarget.checked })
                      }
                    />
                    <span className="pl-6 text-xs text-fg-faint">{kutu.aciklama}</span>
                  </div>
                ))}
              </fieldset>

              <fieldset className="flex flex-col gap-3 border-t border-border pt-4">
                <legend className="text-sm font-medium text-fg-strong">
                  Kritik olduğunda ayrıca
                </legend>
                <div className="flex flex-col gap-1">
                  <Checkbox
                    label="Organizasyon Yönetimi"
                    checked={taslak.escalateToOrgManagementOnCritical}
                    disabled={!degistirebilir}
                    onChange={(e) =>
                      setTaslak({
                        ...taslak,
                        escalateToOrgManagementOnCritical: e.currentTarget.checked,
                      })
                    }
                  />
                  <span className="pl-6 text-xs text-fg-faint">
                    Yalnız kritik tarih türlerinde (bitiş, fesih bildirimi, yenileme…).
                  </span>
                </div>
              </fieldset>

              {/*
                ⚠ SINIR AÇIKÇA SÖYLENİYOR: politika alıcı kümesini genişletir ama görme
                hakkı vermez. Sessiz bıraksaydık yönetici "işaretledim, neden gitmedi?"
                sorusunu cevaplayamazdı.
              */}
              <p className="text-sm text-fg-soft">
                Uyarı yalnız sözleşmeye erişimi olan kişilere gider; bu seçim erişim hakkı vermez.
              </p>
              {degistirebilir ? null : (
                <p className="text-sm text-fg-faint">
                  Politikayı değiştirmek için organizasyon güvenlik yetkisi gerekiyor.
                </p>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
